import { motion } from "framer-motion";
import PropTypes from "prop-types";
import { STAGGER_VARIANTS } from "../../../../constants/animations";

/**
 * StaggerContainer - Container for staggered child animations
 * 
 * Automatically animates children sequentially with a stagger effect.
 * Perfect for lists, word-by-word text animations, or any content
 * that should appear in sequence.
 * 
 * @component
 * @example
 * ```jsx
 * import { StaggerContainer } from "../ui/Motion";
 * import { motion } from "framer-motion";
 * 
 * // Basic usage
 * <StaggerContainer>
 *   {items.map((item, i) => (
 *     <motion.div key={i} variants={STAGGER_VARIANTS.item}>
 *       {item}
 *     </motion.div>
 *   ))}
 * </StaggerContainer>
 * 
 * // Custom stagger delay
 * <StaggerContainer staggerDelay={0.2}>
 *   {words.map((word, i) => (
 *     <motion.span key={i} variants={STAGGER_VARIANTS.item}>
 *       {word}{" "}
 *     </motion.span>
 *   ))}
 * </StaggerContainer>
 * ```
 */
export const StaggerContainer = ({
    children,
    staggerDelay = 0.1,
    delayChildren = 0.2,
    className,
    style,
    as = "div",
    ...props
}) => {
    const MotionComponent = motion[as] || motion.div;

    const customVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: staggerDelay,
                delayChildren: delayChildren
            }
        }
    };

    return (
        <MotionComponent
            variants={customVariants}
            initial="hidden"
            animate="visible"
            className={className}
            style={style}
            {...props}
        >
            {children}
        </MotionComponent>
    );
};

StaggerContainer.propTypes = {
    children: PropTypes.node.isRequired,
    staggerDelay: PropTypes.number,
    delayChildren: PropTypes.number,
    className: PropTypes.string,
    style: PropTypes.object,
    as: PropTypes.string
};

export default StaggerContainer;
